import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Grid,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  EventAvailable as EventIcon,
} from "@mui/icons-material";

const Events = ({ user }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyAuthAndFetchData = async () => {
      // Proceed with data fetching for admins
      try {
        const response = await axios.get(`/api/db/getevents`, {
          withCredentials: true,
        });
        setEvents(response.data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuthAndFetchData();
  }, [user, navigate]);

  const calculateApplications = (applications) => ({
    total: applications.length,
    incomplete: applications.filter((app) => app.status === "incomplete")
      .length,
    accepted: applications.filter((app) => app.status === "accepted").length,
    denied: applications.filter((app) => app.status === "denied").length,
    unreviewed: applications.filter((app) => app.status === "unreviewed")
      .length,
  });

  const handleCreateEvent = () => navigate("/create-event");

  const StatusChip = ({ label, value, color }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Chip
        label={label}
        variant="outlined"
        color={color}
        sx={{ minWidth: 100 }}
      />
      <Typography variant="body1" fontWeight="500">
        {value}
      </Typography>
    </Box>
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          animation: "fadeIn 0.3s ease-in",
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
          Event Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateEvent}
          sx={{ py: 1.5 }}
        >
          Create New Event
        </Button>
      </Box>

      {events.length > 0 ? (
        <Grid container spacing={3}>
          {events.map((event) => {
            const stats = calculateApplications(event.applications);
            return (
              <Grid item xs={12} md={6} lg={4} key={event.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardHeader
                    avatar={<EventIcon color="primary" />}
                    action={
                      <Box>
                        <IconButton
                          onClick={() => console.log(`Edit ${event.id}`)}
                        >
                          <EditIcon color="primary" />
                        </IconButton>
                        <IconButton
                          onClick={() => console.log(`Delete ${event.id}`)}
                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Box>
                    }
                    title={
                      <Typography variant="h6" component="div">
                        {event.name}
                      </Typography>
                    }
                    subheader={`Event ID: ${event.id}`}
                  />
                  <Divider />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      {event.description}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        Application Statistics
                      </Typography>
                      <StatusChip
                        label="Total"
                        value={stats.total}
                        color="primary"
                      />
                      <StatusChip
                        label="Incomplete"
                        value={stats.incomplete}
                        color="warning"
                      />
                      <StatusChip
                        label="Accepted"
                        value={stats.accepted}
                        color="success"
                      />
                      <StatusChip
                        label="Denied"
                        value={stats.denied}
                        color="error"
                      />
                      <StatusChip
                        label="Unreviewed"
                        value={stats.unreviewed}
                        color="info"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <EventIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No events available. Create your first event!
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Events;
